// Admin School Logos — review the auto-fetched crests, replace the wrong ones.
// File: frontend/src/pages/AdminSchoolLogosPage.js
//
// Consumes /api/admin/school-logos/{suspect,search,athlete/:id,replace}
// (backend/app/routers/admin_school_logos.py).
//
// WHY THE CONTACT SHEET IS THE PAGE, AND SEARCH IS SECONDARY. The logos that
// are wrong were acquired by handing a school NAME to logo.dev, which resolves
// it to a company domain and returns that company's brand mark — "Hardin
// Calhoun" became Calhoun Liberty Hospital. Nothing in the data marks the bad
// ones: the row looks identical whether the mark belongs to the school or to a
// hospital in another state. The only detector is a human recognising a brand,
// and recognition is fast in a grid and slow one athlete at a time. So the
// default view is every unreviewed logo at once, biggest audience first, and
// the search box is for when you already know which school you came to fix.
//
// TWO THINGS THIS FILE IS CAREFUL ABOUT:
//
// 1. A replace is SCHOOL-WIDE. The confirm line says how many athletes are on
//    the logo before the click, and the result line says how many actually
//    moved after it. Neither is a guess — both come off the server.
// 2. A personal override is called out by name. An athlete who uploaded their
//    own crest is not on the shared URL, so a school-wide replace leaves them
//    untouched; when you arrive here from such an athlete, the page says so
//    rather than letting you conclude the fix didn't work.
//
// Thumbnails go through the same-origin image proxy for the reason
// RailCards.jsx does: direct S3 <img src> is blocked by some corporate filters
// and ad-blockers, and a blocked crest looks exactly like a missing one.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import AdminPage from '../components/AdminShell';
import './AdminSchoolLogosPage.css';

const PAGE_SIZE = 60;

// How each source got here, in words an admin can act on. `logo_dev` is the
// one that produces hospitals; the others are stated plainly so a correct
// logo isn't replaced out of suspicion of its label.
const SOURCE_NOTE = {
  logo_dev: 'Guessed from the school name by logo.dev — this is the source that returns company logos.',
  hudl: 'The school\'s own upload to its Hudl page. Imported in bulk; generally correct.',
  admin_upload: 'Uploaded by an admin.',
  admin_url: 'Set by an admin from an image URL.',
  admin_cleared: 'Cleared by an admin. No logo will show, and the fetcher will not re-add one.',
};

function proxied(apiUrl, url) {
  return url ? `${apiUrl}/image-proxy?url=${encodeURIComponent(url)}` : null;
}

function initialsOf(name) {
  return (name || '').split(/\s+/).filter(Boolean).slice(0, 2)
    .map((w) => w[0]).join('').toUpperCase() || '?';
}

/** A crest that degrades to initials instead of a broken-image glyph. */
function Crest({ url, schoolName, apiUrl, large }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => { setFailed(false); }, [url]);
  const src = proxied(apiUrl, url);
  const cls = large ? 'lgo-crest lgo-crest--lg' : 'lgo-crest';
  if (!src || failed) {
    return <span className={`${cls} lgo-crest--fallback`}>{initialsOf(schoolName)}</span>;
  }
  return (
    <span className={cls}>
      <img src={src} alt="" loading="lazy" onError={() => setFailed(true)} />
    </span>
  );
}

export default function AdminSchoolLogosPage() {
  const { API_URL } = useAuth();
  const [params, setParams] = useSearchParams();

  const [suspect, setSuspect] = useState(null);      // { total, logos }
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [query, setQuery] = useState('');
  const [queryState, setQueryState] = useState('');
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);

  const [selected, setSelected] = useState(null);    // a serialized logo row
  const [override, setOverride] = useState(null);    // athlete-override notice
  const [outcome, setOutcome] = useState(null);      // last replace result

  // ── the review sheet ──────────────────────────────────────────────
  const loadSuspect = useCallback(async (nextOffset) => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API_URL}/admin/school-logos/suspect`, {
        params: { limit: PAGE_SIZE, offset: nextOffset },
      });
      setSuspect(res.data);
      setOffset(nextOffset);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not load the logo list.');
    } finally {
      setLoading(false);
    }
  }, [API_URL]);

  useEffect(() => { loadSuspect(0); }, [loadSuspect]);

  // ── deep links ────────────────────────────────────────────────────
  // ?athlete=1181           — arrived from an athlete profile
  // ?school=NAME&state=IL   — arrived from anywhere else
  const deepAthlete = params.get('athlete');
  const deepSchool = params.get('school');
  const deepState = params.get('state');
  const deepDone = useRef(false);

  useEffect(() => {
    if (deepDone.current) return;
    if (!deepAthlete && !deepSchool) return;
    deepDone.current = true;

    (async () => {
      try {
        if (deepAthlete) {
          const res = await axios.get(`${API_URL}/admin/school-logos/athlete/${deepAthlete}`);
          const d = res.data;
          setQuery(d.high_school || '');
          setQueryState(d.high_school_state || '');
          if (d.shared_logo) {
            setSelected(d.shared_logo);
          } else if (d.high_school) {
            // No shared row yet — offer to create one for this school
            // rather than making the admin retype what we already know.
            setSelected({
              school_name: (d.high_school || '').toUpperCase(),
              state: (d.high_school_state || d.profile_state || '').toUpperCase(),
              logo_url: null, cleared: false, source: null, source_url: null,
              athlete_count: 0, unsaved: true,
            });
          }
          if (d.has_personal_override) {
            setOverride({
              userId: d.user_id,
              url: d.profile_school_logo,
            });
          }
        } else {
          setQuery(deepSchool);
          setQueryState(deepState || '');
          const res = await axios.get(`${API_URL}/admin/school-logos/search`, {
            params: { q: deepSchool, state: deepState || undefined, limit: 20 },
          });
          const hit = (res.data.logos || []).find(
            (l) => l.school_name === deepSchool.toUpperCase()
              && (!deepState || l.state === deepState.toUpperCase()),
          ) || (res.data.logos || [])[0];
          if (hit) setSelected(hit);
          else {
            setSelected({
              school_name: deepSchool.toUpperCase(),
              state: (deepState || '').toUpperCase(),
              logo_url: null, cleared: false, source: null, source_url: null,
              athlete_count: 0, unsaved: true,
            });
          }
        }
      } catch (e) {
        setError(e.response?.data?.detail || 'Could not open that school.');
      }
    })();
  }, [API_URL, deepAthlete, deepSchool, deepState]);

  // ── search ────────────────────────────────────────────────────────
  const runSearch = async (e) => {
    if (e) e.preventDefault();
    const q = query.trim();
    if (q.length < 2) {
      setError('Search needs at least 2 characters.');
      return;
    }
    setSearching(true);
    setError(null);
    try {
      const res = await axios.get(`${API_URL}/admin/school-logos/search`, {
        params: { q, state: queryState.trim() || undefined, limit: 100 },
      });
      setResults(res.data.logos || []);
    } catch (err) {
      setError(err.response?.data?.detail || 'Search failed.');
    } finally {
      setSearching(false);
    }
  };

  const pick = (logo) => {
    setSelected(logo);
    setOutcome(null);
    setOverride(null);
    setParams({}, { replace: true });
  };

  const afterReplace = (result) => {
    setOutcome(result);
    setSelected({ ...result.logo, unsaved: false });
    // The sheet shrinks by one — that shrinking is the progress bar.
    loadSuspect(offset);
    if (results) {
      setResults(results.map((r) => (
        r.school_name === result.logo.school_name && r.state === result.logo.state
          ? result.logo : r
      )));
    }
  };

  const total = suspect?.total || 0;
  const showing = suspect?.logos || [];

  return (
    <AdminPage
      active="school-logos"
      title="School Logos"
      subtitle="Review the crests we fetched automatically, and replace the ones that are wrong."
    >
      {error && <div className="adm-error">{error}</div>}

      {override && (
        <div className="adm__card lgo-panel lgo-override">
          <strong>Heads up —</strong> athlete #{override.userId} uploaded their own
          school logo, so it is not the shared one. Replacing the school logo below
          will <em>not</em> change what that athlete&rsquo;s profile shows. To change
          theirs, edit that athlete&rsquo;s photos directly.
        </div>
      )}

      {selected && (
        <LogoEditor
          key={`${selected.school_name}/${selected.state}`}
          logo={selected}
          apiUrl={API_URL}
          onDone={afterReplace}
          onClose={() => { setSelected(null); setOutcome(null); setOverride(null); }}
          outcome={outcome}
        />
      )}

      <div className="adm__card lgo-panel">
        <div className="adm-card__head">
          <div>
            <h2 className="adm-card__title">
              Auto-fetched logos {total > 0 && <span className="lgo-count">{total}</span>}
            </h2>
            <p className="adm-card__sub">
              These were downloaded by machine and nobody has looked at them. Most are
              right. The wrong ones are company logos: a name like &ldquo;Calhoun&rdquo;
              gets resolved to a business domain, so you will see hospitals, banks and
              car dealers among the crests. Sorted by how many athletes see each one.
            </p>
          </div>
        </div>

        {loading && <div className="adm-loading">Loading logos…</div>}
        {!loading && showing.length === 0 && (
          <div className="adm-empty">
            Nothing left to review — every auto-fetched logo has been looked at.
          </div>
        )}

        {!loading && showing.length > 0 && (
          <>
            <div className="lgo-sheet">
              {showing.map((l) => (
                <button
                  type="button"
                  key={`${l.school_name}/${l.state}`}
                  className="lgo-tile"
                  onClick={() => pick(l)}
                  title={l.source_url || l.source || ''}
                >
                  <Crest url={l.logo_url} schoolName={l.school_name} apiUrl={API_URL} />
                  <span className="lgo-tile__name">{l.school_name}</span>
                  <span className="lgo-tile__meta">
                    {l.state}
                    {l.athlete_count > 0 && (
                      <> · {l.athlete_count} athlete{l.athlete_count === 1 ? '' : 's'}</>
                    )}
                  </span>
                </button>
              ))}
            </div>
            <div className="adm-pager">
              <button
                className="adm-btn" disabled={offset === 0 || loading}
                onClick={() => loadSuspect(Math.max(0, offset - PAGE_SIZE))}
              >
                Previous
              </button>
              <span className="adm-note">
                {offset + 1}–{Math.min(offset + showing.length, total)} of {total}
              </span>
              <button
                className="adm-btn" disabled={offset + PAGE_SIZE >= total || loading}
                onClick={() => loadSuspect(offset + PAGE_SIZE)}
              >
                Next
              </button>
            </div>
          </>
        )}
      </div>

      <div className="adm__card lgo-panel">
        <div className="adm-card__head">
          <div>
            <h2 className="adm-card__title">Find any school</h2>
            <p className="adm-card__sub">
              Search all schools that have a logo on file, including ones already
              reviewed. A school with no logo yet won&rsquo;t appear — open it from an
              athlete&rsquo;s profile instead and this page will offer to create one.
            </p>
          </div>
        </div>

        <form className="adm-toolbar adm-toolbar--incard" onSubmit={runSearch}>
          <div className="adm-field">
            <label className="adm-field__label" htmlFor="lgo-q">School name</label>
            <input
              id="lgo-q" className="adm-input" value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Hardin Calhoun"
            />
          </div>
          <div className="adm-field">
            <label className="adm-field__label" htmlFor="lgo-st">State</label>
            <input
              id="lgo-st" className="adm-input lgo-state-input" value={queryState}
              onChange={(e) => setQueryState(e.target.value)}
              placeholder="IL" maxLength={2}
            />
          </div>
          <button className="adm-btn adm-btn--primary" type="submit" disabled={searching}>
            {searching ? 'Searching…' : 'Search'}
          </button>
        </form>

        {results && results.length === 0 && (
          <div className="adm-empty">No school with a logo matches that.</div>
        )}
        {results && results.length > 0 && (
          <div className="adm-tablewrap">
            <table className="adm-table">
              <thead>
                <tr>
                  <th>Logo</th><th>School</th><th>State</th>
                  <th className="adm-th--right">Athletes</th><th>Source</th><th />
                </tr>
              </thead>
              <tbody>
                {results.map((l) => (
                  <tr key={`${l.school_name}/${l.state}`}>
                    <td><Crest url={l.logo_url} schoolName={l.school_name} apiUrl={API_URL} /></td>
                    <td className="adm-cell__name">{l.school_name}</td>
                    <td>{l.state}</td>
                    <td className="adm-td--num">{l.athlete_count}</td>
                    <td className="adm-cell__meta">{l.cleared ? 'cleared' : (l.source || 'bulk load')}</td>
                    <td className="adm-td--right">
                      <button className="adm-btn adm-btn--sm" onClick={() => pick(l)}>Open</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminPage>
  );
}

/**
 * The replace panel for one school.
 *
 * Three mutually exclusive modes, because the server accepts exactly one of
 * them and a UI that let you arm two would be lying about what it was going
 * to do. `clear` is a first-class mode rather than a destructive afterthought:
 * a blank crest is a correct answer when we simply do not have the school's
 * logo, and it is the only one that stops the fetcher re-adding a wrong one.
 */
function LogoEditor({ logo, apiUrl, onDone, onClose, outcome }) {
  const [mode, setMode] = useState('upload');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);

  const onFile = (e) => {
    const f = e.target.files?.[0] || null;
    setFile(f);
    if (preview) URL.revokeObjectURL(preview);
    setPreview(f ? URL.createObjectURL(f) : null);
  };

  const canSubmit = mode === 'clear'
    || (mode === 'upload' && !!file)
    || (mode === 'url' && url.trim().length > 0);

  const submit = async () => {
    setBusy(true);
    setErr(null);
    try {
      const body = new FormData();
      body.append('school_name', logo.school_name);
      body.append('state', logo.state);
      if (mode === 'clear') body.append('clear', 'true');
      else if (mode === 'url') body.append('image_url', url.trim());
      else body.append('file', file);

      const res = await axios.post(`${apiUrl}/admin/school-logos/replace`, body);
      setFile(null);
      if (preview) URL.revokeObjectURL(preview);
      setPreview(null);
      setUrl('');
      onDone(res.data);
    } catch (e) {
      setErr(e.response?.data?.detail || 'The replace failed.');
    } finally {
      setBusy(false);
    }
  };

  const count = logo.athlete_count || 0;
  const verb = mode === 'clear' ? 'Clear the logo' : 'Replace the logo';

  return (
    <div className="adm__card lgo-panel lgo-editor">
      <div className="adm-card__head">
        <div>
          <h2 className="adm-card__title">
            {logo.school_name} <span className="lgo-editor__state">{logo.state}</span>
          </h2>
          <p className="adm-card__sub">
            {logo.unsaved
              ? 'No logo on file for this school yet — setting one here creates it.'
              : `${count} athlete${count === 1 ? '' : 's'} currently ${count === 1 ? 'shows' : 'show'} this logo.`}
          </p>
        </div>
        <button className="adm-btn adm-btn--sm" onClick={onClose}>Close</button>
      </div>

      {outcome && (
        <div className="lgo-outcome">
          <strong>Done.</strong>{' '}
          {outcome.logo.cleared
            ? 'The logo is cleared and will not come back on its own.'
            : 'The new logo is live.'}{' '}
          {outcome.athletes_moved} athlete{outcome.athletes_moved === 1 ? '' : 's'} moved
          off the old logo
          {outcome.athletes_filled > 0 && (
            <>, and {outcome.athletes_filled} who had none now {outcome.athletes_filled === 1 ? 'has' : 'have'} one</>
          )}.
        </div>
      )}

      <div className="lgo-editor__grid">
        <div className="lgo-editor__current">
          <span className="adm-field__label">Currently</span>
          {logo.cleared ? (
            <div className="lgo-cleared">Cleared — no logo shows</div>
          ) : (
            <Crest url={logo.logo_url} schoolName={logo.school_name} apiUrl={apiUrl} large />
          )}
        </div>

        <div className="lgo-editor__new">
          <span className="adm-field__label">Replace with</span>
          <div className="adm-tabs lgo-modes">
            {[
              ['upload', 'Upload a file'],
              ['url', 'Paste an image URL'],
              ['clear', 'Clear it'],
            ].map(([key, label]) => (
              <button
                key={key} type="button"
                className={`adm-tab${mode === key ? ' adm-tab--active' : ''}`}
                onClick={() => { setMode(key); setErr(null); }}
              >
                {label}
              </button>
            ))}
          </div>

          {mode === 'upload' && (
            <div className="lgo-mode-body">
              <input type="file" accept="image/*" onChange={onFile} className="lgo-file" />
              {preview && (
                <span className="lgo-crest lgo-crest--lg"><img src={preview} alt="New logo preview" /></span>
              )}
              <p className="adm-note">
                PNG, JPG, WebP or SVG up to 10 MB. It gets trimmed and scaled to 512px.
              </p>
            </div>
          )}

          {mode === 'url' && (
            <div className="lgo-mode-body">
              <input
                className="adm-input" value={url} onChange={(e) => setUrl(e.target.value)}
                placeholder="https://…/crest.png"
              />
              <p className="adm-note">
                We download it once and store our own copy, so the original can move
                or disappear without breaking anything.
              </p>
            </div>
          )}

          {mode === 'clear' && (
            <div className="lgo-mode-body">
              <p className="adm-note adm-note--warn">
                No logo will show for this school. This sticks — the automatic fetcher
                will not go and find another one.
              </p>
            </div>
          )}

          {err && <div className="adm-error">{err}</div>}

          <button
            className={`adm-btn lgo-submit ${mode === 'clear' ? 'adm-btn--danger' : 'adm-btn--primary'}`}
            disabled={!canSubmit || busy}
            onClick={submit}
          >
            {busy ? 'Working…' : `${verb} for all ${count || 'athletes at this'} ${count ? `athlete${count === 1 ? '' : 's'}` : 'school'}`}
          </button>
        </div>
      </div>

      {/* Provenance runs the full width of the panel rather than sitting in
          the 180px crest column. It is the one piece of text that actually
          identifies a wrong logo -- "calhounlibertyhospital.com" is the whole
          tell -- and in a narrow column it wrapped mid-word into gibberish. */}
      {(logo.source || logo.source_url) && (
        <div className="lgo-provenance">
          {logo.source && (
            <p className="adm-note">{SOURCE_NOTE[logo.source] || `Source: ${logo.source}`}</p>
          )}
          {logo.source_url && (
            <p className="adm-note lgo-sourceurl">
              Fetched from <span>{logo.source_url}</span>
            </p>
          )}
        </div>
      )}

      <p className="adm-note">
        This logo is shared by everyone at this school. An athlete who uploaded
        their own is not affected — see{' '}
        <Link className="adm-link" to="/admin/users">Users</Link> to open a
        specific athlete.
      </p>
    </div>
  );
}
